import * as pc from "playcanvas";

/**
 * AssetGate — knows when EVERY asset the app enqueued has finished
 * downloading. Each loader in the app (arena textures, prop GLBs, the HDRI,
 * the character + animation GLBs, all audio) goes through
 * `app.assets.loadFromUrl`, so the PlayCanvas registry IS the complete
 * download manifest — polling it covers everything with no per-module wiring.
 *
 * Used for the match-start load gate: `main.ts` awaits `whenLoaded()` and
 * then reports `client_ready` to the game-server, which holds the match in
 * warmup until both clients have reported (no more VS intro over a
 * half-loaded arena when an opponent is already waiting in the queue).
 *
 * Failed loads count as settled (a missing texture must not hold the match
 * hostage) and `maxWaitMs` caps the whole wait — on a CDN stall the client
 * reports ready anyway and the match starts exactly like it used to.
 *
 * Construct EARLY (right after the pc.Application) so the registry error
 * listener sees failures from the very first loads; call `whenLoaded()` late
 * (after every loader has enqueued its downloads).
 */
export class AssetGate {
  private readonly app: pc.AppBase;
  /** Ids of assets whose load errored — `asset.loaded` stays false forever
   *  for those, so they're tracked separately and counted as settled. */
  private readonly errored = new Set<number>();

  constructor(app: pc.AppBase) {
    this.app = app;
    this.app.assets.on("error", (_err: string, asset: pc.Asset) => {
      if (asset && typeof asset.id === "number") this.errored.add(asset.id);
    });
  }

  /** Registry assets still downloading (not loaded, not errored). */
  private pending(): pc.Asset[] {
    return this.app.assets.filter((a) => !a.loaded && !this.errored.has(a.id));
  }

  /**
   * Resolves once the registry has fully settled (two consecutive empty
   * polls — a just-finished container can enqueue follow-up loads, so a
   * single instantaneous gap isn't trusted), or after `maxWaitMs`. Never
   * rejects.
   */
  whenLoaded(maxWaitMs = 20_000): Promise<void> {
    return new Promise((resolve) => {
      const startedAt = performance.now();
      let settledPolls = 0;
      const poll = window.setInterval(() => {
        const pending = this.pending();
        if (pending.length === 0) {
          settledPolls++;
          if (settledPolls >= 2) {
            window.clearInterval(poll);
            resolve();
          }
          return;
        }
        settledPolls = 0;
        if (performance.now() - startedAt >= maxWaitMs) {
          console.warn(
            `[AssetGate] timed out with ${pending.length} asset(s) still loading — reporting ready anyway`,
            pending.slice(0, 5).map((a) => a.name || a.id),
          );
          window.clearInterval(poll);
          resolve();
        }
      }, 150);
    });
  }
}

/**
 * Warm a set of DOM image urls (HUD icon PNGs — those render via `<img>`,
 * not the PlayCanvas registry) into the browser cache. Settles when every
 * image has either loaded or errored; never rejects.
 */
export function preloadImages(urls: ReadonlyArray<string>): Promise<void> {
  return Promise.all(
    urls.map(
      (url) =>
        new Promise<void>((resolve) => {
          const img = new Image();
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = url;
        }),
    ),
  ).then(() => undefined);
}
